import { isKnowledgeSourceSuggestionCard } from '@/services/review/card-filters'
import { localDb } from '@/data/local/database'
import { createEntityId, putLocalEntity, queueLocalEntity, softDeleteLocalEntity } from '@/data/local/mutations'
import { buildComprehensiveStats, buildPeriodStats, toLegacyPractice, toLegacySession } from '@/data/local/stats'
import type {
  InspirationTag,
  Note,
  PracticeRecord,
  QuestionOptionKey,
  QuestionOptions,
  RecurringTodo,
  StudySession,
  Todo,
  UserSettings,
  VersionedEntity,
} from '@/domain/types'
import { formatLocalDate } from '@/lib/date'
import { ensureLocalIdentity } from '@/services/identity/local-identity'
import { remoteApiRequest } from '@/data/api/remote-api'
import { sanitizeRichTextClient } from '@/lib/sanitize-rich-text-client'
import { rateNote, type ReviewRating } from '@/services/review/fsrs-scheduler'
import { noteSemanticKey } from '@/services/sync/note-dedup'
import { notebookProgress, notebookVisit } from '@/data/local/notebook-practice'
import { handleLocalNotebookApi } from './notebook-api'
import { patchLocalNotebook } from '@/data/local/notebooks'

const REVIEW_INTERVALS = {
  again: 1,
  hard: 3,
  good: 7,
  easy: 30,
} as const

const REVIEW_RATING_MAP: Record<keyof typeof REVIEW_INTERVALS, ReviewRating> = {
  again: 1,
  hard: 2,
  good: 3,
  easy: 4,
}

type JsonRecord = Record<string, unknown>

function json(data: unknown, status = 200) {
  return new Response(JSON.stringify(data), {
    status,
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
  })
}

async function bodyOf(request: Request): Promise<JsonRecord> {
  if (request.method === 'GET' || request.method === 'HEAD') return {}
  try {
    return await request.json() as JsonRecord
  } catch {
    return {}
  }
}

function text(value: unknown, fallback = '') {
  return typeof value === 'string' ? value.trim() : fallback
}

function number(value: unknown, fallback = 0) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function stringList(value: unknown) {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

const QUESTION_OPTION_KEYS: QuestionOptionKey[] = ['A', 'B', 'C', 'D']

function questionOptions(value: unknown): QuestionOptions {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const source = value as Record<string, unknown>
  return Object.fromEntries(
    QUESTION_OPTION_KEYS.map(key => [key, text(source[key])]).filter(([, option]) => Boolean(option)),
  ) as QuestionOptions
}

function questionOptionKey(value: unknown): QuestionOptionKey | undefined {
  return QUESTION_OPTION_KEYS.includes(value as QuestionOptionKey) ? value as QuestionOptionKey : undefined
}

function versioned(identity: Awaited<ReturnType<typeof ensureLocalIdentity>>, id: string, now: number): VersionedEntity {
  return {
    id,
    workspaceId: identity.workspaceId,
    ownerUserId: identity.userId,
    createdAtMs: now,
    updatedAtMs: now,
    deletedAtMs: null,
    version: 0,
  }
}

function active<T extends VersionedEntity>(items: T[], workspaceId: string) {
  return items.filter(item => item.workspaceId === workspaceId && !item.deletedAtMs)
}

function noteTags(item: Pick<Note, 'tags'>) {
  return Array.isArray(item.tags) ? item.tags : []
}

function isMistakeNote(item: Note) {
  return item.category === '错题记录' || item.type === 'mistake' || noteTags(item).includes('错题记录')
}

function hasReviewableContent(item: Note) {
  return Boolean(
    (typeof item.title === 'string' && item.title.trim())
    || (typeof item.content === 'string' && item.content.replace(/<[^>]*>/g, '').trim())
    || (typeof item.questionImage === 'string' && item.questionImage.trim()),
  )
}

/**
 * 复习到期时间（ms）。FSRS 评审以 reviewState.dueAt 为准；遗留字段
 * nextReviewAtMs 仅作兜底——两个字段不同步会导致「闪卡库显示逾期，
 * 复习队列却为空」的假象（旧客户端评分只写 nextReviewAtMs）。
 */
function noteDueAtMs(note: Note): number | null {
  if (note.reviewState?.dueAt) {
    const parsed = new Date(note.reviewState.dueAt).getTime()
    if (Number.isFinite(parsed)) return parsed
  }
  return note.nextReviewAtMs ?? null
}

/** 从未开始（无 FSRS 状态或 new）的卡视为立即到期，进入复习队列。 */
function noteIsNew(note: Note): boolean {
  return !note.reviewState || note.reviewState.state === 'new'
}

function legacyNote(note: Note) {
  return {
    id: note.id,
    title: note.title,
    content: note.content,
    category: note.category,
    tags: note.tags,
    source: note.source,
    review_stage: note.reviewStage,
    review_count: note.reviewCount,
    next_review_at: note.nextReviewAtMs ? new Date(note.nextReviewAtMs).toISOString() : null,
    last_reviewed_at: note.lastReviewedAtMs ? new Date(note.lastReviewedAtMs).toISOString() : null,
    created_at: new Date(note.createdAtMs).toISOString(),
    updated_at: new Date(note.updatedAtMs).toISOString(),
    isFavorite: note.isFavorite || false,
    reviewState: note.reviewState || null,
    type: note.type,
    recordKind: note.recordKind,
    questionImage: note.questionImage,
    questionHasVisuals: note.questionHasVisuals,
    questionOptions: note.questionOptions,
    correctOption: note.correctOption,
    answerMode: note.answerMode,
    handwritingImage: note.handwritingImage,
    canvasRemark: note.canvasRemark,
    notebookModule: note.notebookModule,
    notebookBook: note.notebookBook,
    notebookInk: note.notebookInk,
    notebookLayout: note.notebookLayout,
    notebookQuestionInk: note.notebookQuestionInk,
  }
}

function legacyTodo(todo: Todo) {
  return {
    id: todo.id,
    title: todo.title,
    notes: todo.notes,
    task_date: todo.taskDate,
    scheduled_time: todo.scheduledTime,
    estimate_minutes: todo.estimateMinutes,
    priority: todo.priority,
    focus_module: todo.focusModule,
    completed: todo.completed ? 1 : 0,
    created_at: new Date(todo.createdAtMs).toISOString(),
  }
}

function legacyRecurring(todo: RecurringTodo) {
  return {
    id: todo.id,
    title: todo.title,
    notes: todo.notes,
    scheduled_time: todo.scheduledTime,
    estimate_minutes: todo.estimateMinutes,
    focus_module: todo.focusModule,
    weekdays: todo.weekdays,
    recurrence_type: todo.recurrenceType,
    custom_dates: todo.customDates,
    active: todo.active ? 1 : 0,
    created_at: new Date(todo.createdAtMs).toISOString(),
  }
}

function dueOn(todo: RecurringTodo, date: string) {
  if (!todo.active) return false
  if (todo.recurrenceType === 'custom_dates') return todo.customDates.includes(date)
  const day = new Date(`${date}T12:00:00`).getDay() || 7
  return todo.recurrenceType === 'daily' || todo.weekdays.includes(day)
}

async function handleAuth(path: string, request: Request) {
  const identity = await ensureLocalIdentity()
  const settings = await localDb.userSettings.get(identity.userId)
  if (path === '/api/auth/me') {
    return json({
      user: {
        id: identity.userId,
        name: settings?.displayName || '本地用户',
        displayName: settings?.displayName || '本地用户',
        email: settings?.email || '',
        statusSignature: settings?.statusSignature || '',
        examName: settings?.examName || '',
        examDate: settings?.examDate || '',
        mockName: settings?.mockName || '',
        mockDate: settings?.mockDate || '',
        examType: settings?.examType || '',
        studyModules: settings?.studyModules || [],
        focusTheme: settings?.focusTheme || 'standard',
        authenticated: identity.authenticated,
      },
    })
  }
  if (path === '/api/auth/logout' && request.method === 'POST') return json({ ok: true })
  return json({ error: '请通过同步中心使用邀请码登录' }, 404)
}

async function handleFocus(url: URL, request: Request) {
  const identity = await ensureLocalIdentity()
  if (request.method === 'GET') {
    const date = url.searchParams.get('date') || formatLocalDate()
    const sessions = active(await localDb.studySessions.toArray(), identity.workspaceId)
      .filter(item => formatLocalDate(new Date(item.startedAtMs)) === date && item.status === 'completed')
      .sort((a, b) => b.startedAtMs - a.startedAtMs)
    return json({
      sessions: sessions.map(toLegacySession),
      totalMinutes: sessions.reduce((sum, item) => sum + Math.round(item.durationSeconds / 60), 0),
    })
  }
  const body = await bodyOf(request)
  const minutes = Math.max(1, Math.min(1440, Math.round(number(body.minutes, 25))))
  const now = Date.now()
  const entity: StudySession = {
    ...versioned(identity, createEntityId('session'), now),
    // 注意：subject 不再兜底成 focusModule。让"未选具体任务"的专注保留空主题，
    // UI 端会显示为「自主专注」，与"选了某专题任务"的记录在历史列表里清晰区分。
    subject: text(body.subject) || '自主专注',
    focusModule: text(body.focusModule) || '综合',
    startedAtMs: now - minutes * 60_000,
    endedAtMs: now,
    durationSeconds: minutes * 60,
    status: 'completed',
    note: text(body.note),
    reachedTarget: Boolean(body.reachedTarget),
    deviceId: identity.deviceId,
  }
  await putLocalEntity(localDb.studySessions, 'study_session', entity, 'create', null)
  return json({ session: toLegacySession(entity) }, 201)
}

async function handleFocusHeatmap() {
  const identity = await ensureLocalIdentity()
  const sessions = active(await localDb.studySessions.toArray(), identity.workspaceId)
  const daily = new Map<string, number>()
  for (const item of sessions) {
    if (item.status !== 'completed') continue
    const date = formatLocalDate(new Date(item.startedAtMs))
    daily.set(date, (daily.get(date) || 0) + Math.round(item.durationSeconds / 60))
  }
  const data = [...daily].map(([date, minutes]) => ({ date, minutes }))
  return json({ data, minutesByDate: Object.fromEntries(daily) })
}

async function handleTodos(url: URL, request: Request, id?: string) {
  const identity = await ensureLocalIdentity()
  if (id) {
    if (request.method === 'DELETE') {
      await softDeleteLocalEntity(localDb.todos, 'todo', id)
      return json({ ok: true })
    }
    const current = await localDb.todos.get(id)
    if (!current || current.workspaceId !== identity.workspaceId) return json({ error: '待办不存在' }, 404)
    const body = await bodyOf(request)
    const now = Date.now()
    const updated: Todo = {
      ...current,
      title: body.title === undefined ? current.title : text(body.title),
      completed: body.completed === undefined ? current.completed : Boolean(body.completed),
      completedAtMs: body.completed === undefined ? current.completedAtMs : Boolean(body.completed) ? now : null,
      updatedAtMs: now,
    }
    await putLocalEntity(localDb.todos, 'todo', updated, 'update', current.version)
    return json({ todo: legacyTodo(updated) })
  }
  if (request.method === 'GET') {
    const date = url.searchParams.get('date') || formatLocalDate()
    const todos = active(await localDb.todos.toArray(), identity.workspaceId)
      .filter(item => item.taskDate === date)
      .sort((a, b) => a.createdAtMs - b.createdAtMs)
    return json({ todos: todos.map(legacyTodo) })
  }
  const body = await bodyOf(request)
  const title = text(body.title)
  if (!title) return json({ error: '请填写待学内容' }, 400)
  const now = Date.now()
  const entity: Todo = {
    ...versioned(identity, createEntityId('todo'), now),
    title,
    notes: text(body.notes),
    taskDate: text(body.taskDate) || formatLocalDate(),
    scheduledTime: text(body.scheduledTime) || null,
    estimateMinutes: Math.max(1, Math.round(number(body.estimateMinutes, 25))),
    priority: body.priority === 'low' || body.priority === 'high' ? body.priority : 'normal',
    focusModule: text(body.focusModule) || '综合',
    completed: false,
    completedAtMs: null,
  }
  await putLocalEntity(localDb.todos, 'todo', entity, 'create', null)
  return json({ todo: legacyTodo(entity) }, 201)
}

async function handleRecurring(request: Request, id?: string) {
  const identity = await ensureLocalIdentity()
  if (id) {
    if (request.method === 'DELETE') {
      await softDeleteLocalEntity(localDb.recurringTodos, 'recurring_todo', id)
      return json({ ok: true })
    }
    return json({ error: '不支持的操作' }, 405)
  }
  if (request.method === 'GET') {
    const todos = active(await localDb.recurringTodos.toArray(), identity.workspaceId)
      .filter(item => dueOn(item, formatLocalDate()))
      .map(legacyRecurring)
    return json({ todos })
  }
  const body = await bodyOf(request)
  const title = text(body.title)
  if (!title) return json({ error: '请填写待学内容' }, 400)
  const now = Date.now()
  const recurrence = text(body.recurrenceType)
  const entity: RecurringTodo = {
    ...versioned(identity, createEntityId('recurring'), now),
    title,
    notes: text(body.notes),
    scheduledTime: text(body.scheduledTime) || null,
    estimateMinutes: Math.max(1, Math.round(number(body.estimateMinutes, 25))),
    focusModule: text(body.focusModule) || '综合',
    weekdays: Array.isArray(body.weekdays) ? body.weekdays.map(Number).filter(item => item >= 1 && item <= 7) : [],
    recurrenceType: recurrence === 'weekdays' || recurrence === 'custom_dates' ? recurrence : 'daily',
    customDates: stringList(body.customDates),
    active: true,
  }
  await putLocalEntity(localDb.recurringTodos, 'recurring_todo', entity, 'create', null)
  return json({ todo: legacyRecurring(entity) }, 201)
}

async function handleNotes(url: URL, request: Request, id?: string) {
  const identity = await ensureLocalIdentity()
  if (id) {
    if (request.method === 'GET') {
      const note = await localDb.notes.get(id)
      if (!note || note.workspaceId !== identity.workspaceId || note.deletedAtMs) {
        return json({ error: '闪念卡片不存在' }, 404)
      }
      return json({ inspiration: note })
    }
    if (request.method === 'DELETE') {
      await softDeleteLocalEntity(localDb.notes, 'note', id)
      return json({ ok: true })
    }
    if (request.method === 'PATCH') {
      const current = await localDb.notes.get(id)
      if (!current || current.workspaceId !== identity.workspaceId || current.deletedAtMs) {
        return json({ error: '闪念卡片不存在' }, 404)
      }
      const body = await bodyOf(request)
      // Only validate content if it's being updated
      const hasContent = 'content' in body
      // v2.6.7：recordKind 显式传 null 表示从题目记录切回普通卡片（清除 question 类型）
      const nextRecordKind = body.recordKind === undefined ? current.recordKind : (body.recordKind === 'question' ? 'question' : null)
      const nextQuestionImage = 'questionImage' in body ? text(body.questionImage) : current.questionImage
      if (hasContent) {
        const newContent = text(body.content)
        if ((!newContent || newContent === '<p></p>') && nextRecordKind !== 'question') return json({ error: '请输入闪念内容' }, 400)
      }
      if (nextRecordKind === 'question' && !text('title' in body ? body.title : current.title) && !nextQuestionImage) {
        return json({ error: '题目文本和题目图片不能同时为空' }, 400)
      }
      const tags = 'tags' in body ? stringList(body.tags) : current.tags
      const updated: Note = {
        ...current,
        // Only update text fields if provided
        ...(hasContent ? { content: sanitizeRichTextClient(text(body.content)) } : {}),
        ...('title' in body ? { title: text(body.title) } : {}),
        ...('category' in body ? { category: text(body.category) || null } : {}),
        ...('source' in body ? { source: text(body.source) } : {}),
        // 错题判定（v2.3.9 架构调整）：错题记录是独立模块，按 category 判定；旧标签兼容
        type: nextRecordKind === 'question'
          ? 'mistake'
          : (('category' in body ? text(body.category) : current.category) === '错题记录' || tags.includes('错题记录'))
          ? 'mistake'
          : (current.type || 'inspiration'),
        tags,
        ...(body.recordKind !== undefined ? { recordKind: nextRecordKind ?? undefined } : {}),
        ...('questionImage' in body ? { questionImage: nextQuestionImage } : {}),
        ...(body.questionHasVisuals !== undefined ? { questionHasVisuals: Boolean(body.questionHasVisuals) } : {}),
        ...('questionOptions' in body ? { questionOptions: questionOptions(body.questionOptions) } : {}),
        ...('correctOption' in body ? { correctOption: questionOptionKey(body.correctOption) } : {}),
        ...(body.answerMode === 'handwriting' || body.answerMode === 'richtext' ? { answerMode: body.answerMode } : {}),
        ...('handwritingImage' in body ? { handwritingImage: text(body.handwritingImage) } : {}),
        ...('canvasRemark' in body ? { canvasRemark: text(body.canvasRemark) } : {}),
        updatedAtMs: Date.now(),
        // FSRS / favorite fields
        ...(body.reviewState !== undefined ? { reviewState: body.reviewState as Note['reviewState'] } : {}),
        ...(body.reviewCount !== undefined ? { reviewCount: Number(body.reviewCount) } : {}),
        ...(body.nextReviewAtMs !== undefined ? { nextReviewAtMs: Number(body.nextReviewAtMs) } : {}),
        ...(body.lastReviewedAtMs !== undefined ? { lastReviewedAtMs: Number(body.lastReviewedAtMs) } : {}),
        ...(body.isFavorite !== undefined ? { isFavorite: Boolean(body.isFavorite), favoritedAt: body.isFavorite ? new Date().toISOString() : null } : {}),
      }
      const learningStateOnly = Object.keys(body).every(key => ['reviewState', 'reviewCount', 'nextReviewAtMs', 'lastReviewedAtMs', 'isFavorite'].includes(key))
      if (learningStateOnly) await queueLocalEntity(localDb.notes, 'note', updated, 'update', current.version)
      else await putLocalEntity(localDb.notes, 'note', updated, 'update', current.version)
      return json({ inspiration: legacyNote(updated) })
    }
    return json({ error: '不支持的操作' }, 405)
  }
  if (request.method === 'GET') {
    const sourcesType=url.searchParams.get('reviewSources')
    if(sourcesType==='knowledge'||sourcesType==='mistake'){
      const sources=new Set<string>();await localDb.notes.where('type').anyOf('note','knowledge','inspiration','mistake').each(n=>{if(n.workspaceId!==identity.workspaceId||n.deletedAtMs||n.type==='mistake_notebook')return;const mistake=isMistakeNote(n);if((sourcesType==='mistake')!==mistake)return;if(sourcesType==='knowledge'&&!isKnowledgeSourceSuggestionCard(n))return;const source=(n.source||'').trim();if(source)sources.add(source)});return json({sources:[...sources]})
    }
    const category = url.searchParams.get('category')
    const tag = url.searchParams.get('tag')
    const search = (url.searchParams.get('search') || '').toLowerCase()
    const favoriteOnly = url.searchParams.get('favorite') === 'true'
    const notes = active(await (category ? localDb.notes.where('category').equals(category).toArray() : localDb.notes.where('workspaceId').equals(identity.workspaceId).toArray()), identity.workspaceId)
      .filter(item => item.type !== 'mistake_notebook' && (!category || item.category === category)
        && (!tag || noteTags(item).includes(tag))
        && (!search || `${item.title} ${item.content} ${item.source}`.toLowerCase().includes(search))
        && (!favoriteOnly || item.isFavorite === true))
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
    const deletedNoteIds = await localDb.notes.where('deletedAtMs').above(0)
      .filter(note => note.workspaceId === identity.workspaceId).primaryKeys()
    return json({ inspirations: notes.map(legacyNote), deletedNoteIds })
  }
  const body = await bodyOf(request)
  const content = text(body.content)
  const recordKind = body.recordKind === 'question' ? 'question' : undefined
  const questionImage = text(body.questionImage)
  if ((!content || content === '<p></p>') && recordKind !== 'question') return json({ error: '请输入闪念内容' }, 400)
  if (recordKind === 'question' && !text(body.title) && !questionImage) return json({ error: '题目文本和题目图片不能同时为空' }, 400)
  const now = Date.now()
  const entity: Note = {
    ...versioned(identity, createEntityId('note'), now),
    // 错题判定（v2.3.9）：错题记录是独立模块，按 category 判定；旧标签兼容
    type: recordKind === 'question' || text(body.category) === '错题记录' || stringList(body.tags).includes('错题记录') ? 'mistake' : 'inspiration',
    title: text(body.title),
    content: sanitizeRichTextClient(content || ''),
    category: text(body.category) || null,
    tags: stringList(body.tags),
    source: text(body.source),
    reviewStage: 0,
    reviewCount: 0,
    nextReviewAtMs: now,
    lastReviewedAtMs: null,
    ...(recordKind ? { recordKind } : {}),
    ...(questionImage ? { questionImage } : {}),
    ...(body.questionHasVisuals !== undefined ? { questionHasVisuals: Boolean(body.questionHasVisuals) } : {}),
    questionOptions: questionOptions(body.questionOptions),
    ...(questionOptionKey(body.correctOption) ? { correctOption: questionOptionKey(body.correctOption) } : {}),
    answerMode: body.answerMode === 'handwriting' ? 'handwriting' : 'richtext',
    ...(text(body.handwritingImage) ? { handwritingImage: text(body.handwritingImage) } : {}),
  }
  const semanticKey = noteSemanticKey(entity)
  if (semanticKey) {
    const duplicate = (await localDb.notes.where('workspaceId').equals(identity.workspaceId).toArray())
      .find(item => !item.deletedAtMs && noteSemanticKey(item) === semanticKey)
    if (duplicate) return json({ inspiration: legacyNote(duplicate), deduplicated: true })
  }
  await putLocalEntity(localDb.notes, 'note', entity, 'create', null)
  return json({ inspiration: legacyNote(entity) }, 201)
}

async function handleTags(request: Request, id?: string) {
  const identity = await ensureLocalIdentity()
  if (id) {
    if (request.method === 'DELETE') {
      await softDeleteLocalEntity(localDb.inspirationTags, 'inspiration_tag', id)
      return json({ ok: true })
    }
    if (request.method === 'PATCH') {
      const current = await localDb.inspirationTags.get(id)
      if (!current || current.deletedAtMs || current.workspaceId !== identity.workspaceId) {
        return json({ error: '标签不存在' }, 404)
      }
      const body = await bodyOf(request)
      const name = text(body.name).replace(/^#/, '').trim()
      if (!name) return json({ error: '标签名称不能为空' }, 400)
      const duplicate = await localDb.inspirationTags
        .where('[workspaceId+module]')
        .equals([identity.workspaceId, current.module])
        .filter(item => item.id !== id && !item.deletedAtMs && item.name === name)
        .first()
      if (duplicate) return json({ error: '同名标签已存在', duplicateId: duplicate.id }, 409)
      const updated = { ...current, name, updatedAtMs: Date.now() }
      await putLocalEntity(localDb.inspirationTags, 'inspiration_tag', updated, 'update', current.version)
      return json({ tag: { id: updated.id, module: updated.module, name: updated.name } })
    }
    return json({ error: '不支持的操作' }, 405)
  }
  if (request.method === 'GET') {
    const tags = active(await localDb.inspirationTags.toArray(), identity.workspaceId)
      .filter(item => item.name !== '错题记录')
      .sort((a, b) => a.module.localeCompare(b.module) || a.name.localeCompare(b.name))
      .map(item => ({ id: item.id, module: item.module, name: item.name }))
    return json({ tags })
  }
  const body = await bodyOf(request)
  const module = text(body.module)
  const name = text(body.name).replace(/^#/, '')
  if (!module || !name) return json({ error: '模块和标签不能为空' }, 400)
  if (name === '错题记录') return json({ error: '错题记录已是独立一级分类，无需重复创建标签' }, 400)
  const duplicate = await localDb.inspirationTags.where('[workspaceId+module]').equals([identity.workspaceId, module]).filter(item => !item.deletedAtMs && item.name === name).first()
  if (duplicate) return json({ tag: { id: duplicate.id, module, name } })
  const now = Date.now()
  const entity: InspirationTag = { ...versioned(identity, createEntityId('tag'), now), module, name }
  await putLocalEntity(localDb.inspirationTags, 'inspiration_tag', entity, 'create', null)
  return json({ tag: { id: entity.id, module, name } }, 201)
}

async function handleReview(url: URL, request: Request) {
  const identity = await ensureLocalIdentity()
  if (request.method === 'GET') {
    const module = url.searchParams.get('module')
    const source = url.searchParams.get('source')
    const tag = url.searchParams.get('tag') // 部分筛选（v2.3.9：错题记录模块按分部分标签复习）
    const reviewType = url.searchParams.get('type') === 'mistake' ? 'mistake' : 'knowledge'
    const includeAll = url.searchParams.get('all') === 'true'
    const now = Date.now()
    const compact=url.searchParams.get('compact')==='true'
    const favoriteOnly = url.searchParams.get('favorite') === 'true'
    const cards: (ReturnType<typeof legacyNote>&{reviewPreview?:boolean})[]=[]
    await localDb.notes.where('type').anyOf('note','knowledge','inspiration','mistake').each(item => {
        if(item.workspaceId!==identity.workspaceId||item.deletedAtMs||item.type==='mistake_notebook')return
        const include=(()=>{
        if (!hasReviewableContent(item)) return false
        const isMistake = isMistakeNote(item)
        // 每日精读不进入 FSRS 到期队列，但“浏览全部卡片”仍应能看到申论积累卡。
        if (!includeAll && noteTags(item).includes('每日精读')) return false
        const matchesModule = !module
          || item.category === module
          || (item.category === '错题记录' && noteTags(item).includes(module))
        const dueAtMs = noteDueAtMs(item)
        return matchesModule
          && (!favoriteOnly || item.isFavorite === true)
          && (!tag || noteTags(item).includes(tag))
          && (!source || item.source === source)
          && (reviewType === 'mistake' ? isMistake : !isMistake)
          // 到期判定：未开始或按 FSRS dueAt（兜底 nextReviewAtMs）已到期
          && (includeAll || noteIsNew(item) || dueAtMs === null || dueAtMs <= now)
        })();if(include){const card=legacyNote(item);cards.push(compact?{...card,content:'',questionImage:undefined,handwritingImage:undefined,questionOptions:undefined,reviewPreview:true}:card)}
      })
    cards.sort((a,b)=>(Date.parse(a.reviewState?.dueAt||a.next_review_at||'')||0)-(Date.parse(b.reviewState?.dueAt||b.next_review_at||'')||0))
    return json({ cards })
  }
  const body = await bodyOf(request)
  const id = text(body.id)
  const rating = text(body.rating) as keyof typeof REVIEW_INTERVALS
  const current = await localDb.notes.get(id)
  if (!current || current.workspaceId !== identity.workspaceId || !(rating in REVIEW_INTERVALS)) return json({ error: '复习卡片或评分无效' }, 400)
  // 统一走 FSRS-6 评审：与 PATCH 评分一致地写入 reviewState + nextReviewAtMs，
  // 避免旧评分路径只推 nextReviewAtMs 导致「库显示逾期、队列排除」。
  const { note: rated } = rateNote(current, REVIEW_RATING_MAP[rating], null)
  const updated: Note = {
    ...rated,
    reviewStage: rating === 'again' ? 0 : Math.min(12, current.reviewStage + 1),
    updatedAtMs: rated.updatedAtMs,
  }
  await putLocalEntity(localDb.notes, 'note', updated, 'update', current.version)
  return json({ card: legacyNote(updated) })
}

/**
 * 错题笔记本的墨迹与布局沿用 note 同步实体保存，category 使用内部保留值，
 * 因此不会出现在任何普通闪卡/画布模块中，也无需增加第二套同步协议。
 */
async function handleMistakeNotebook(url: URL, request: Request) {
  const identity = await ensureLocalIdentity()
  const module = text(url.searchParams.get('module'))
  if (!module) return json({ error: '请选择错题模块' }, 400)
  const bookId = url.searchParams.get('bookId')
  const current = active(await (bookId ? localDb.notes.where('id').equals(bookId).toArray() : localDb.notes.where('[workspaceId+notebookModule]').equals([identity.workspaceId, module]).toArray()), identity.workspaceId)
    .find(item => item.type === 'mistake_notebook' && (bookId ? item.id === bookId && !!item.notebookBook : !item.notebookBook && item.notebookModule === module))
  if (bookId && !current) return json({ error: '错题本不存在或已删除' }, 404)
  if (request.method === 'GET') {
    const pendingSync = current
      ? await localDb.pendingOperations.where('entityId').equals(current.id)
        .filter(item => item.entityType === 'note')
        .count()
      : 0
    return json({ notebook: current ? legacyNote(current) : null, synced: pendingSync === 0 })
  }
  if (request.method !== 'PUT') return json({ error: '不支持的操作' }, 405)
  const body = await bodyOf(request)
  const now = Date.now()
  if (bookId) {
    const saved = await patchLocalNotebook(bookId, latest => ({
      ...(typeof body.notebookInk === 'string' ? { notebookInk: body.notebookInk } : {}),
      ...(body.notebookLayout && typeof body.notebookLayout === 'object' ? { notebookLayout: body.notebookLayout as Note['notebookLayout'] } : {}),
      ...(body.notebookQuestionInk && typeof body.notebookQuestionInk === 'object' ? { notebookQuestionInk: Object.fromEntries(Object.entries(body.notebookQuestionInk).filter(([, v]) => typeof v === 'string')) as Record<string, string> } : {}),
      notebookBook: latest.notebookBook,
    }))
    return json({ ok: true, notebook: legacyNote(saved), synced: false })
  }
  const layout = body.notebookLayout && typeof body.notebookLayout === 'object'
    ? body.notebookLayout as Record<string, { imageScale: number }>
    : current?.notebookLayout || {}
  const questionInk = body.notebookQuestionInk && typeof body.notebookQuestionInk === 'object'
    ? Object.fromEntries(Object.entries(body.notebookQuestionInk as Record<string, unknown>)
      .filter(([, value]) => typeof value === 'string')) as Record<string, string>
    : current?.notebookQuestionInk || {}
  const entity: Note = current ? {
    ...current,
    notebookInk: text(body.notebookInk, current.notebookInk || ''),
    notebookLayout: layout,
    notebookQuestionInk: questionInk,
    updatedAtMs: now,
  } : {
    ...versioned(identity, createEntityId('notebook'), now),
    type: 'mistake_notebook',
    title: `${module}错题笔记本`,
    content: '<p>错题笔记本手写数据</p>',
    category: '__mistake_notebook__',
    tags: [module],
    source: '',
    reviewStage: 0,
    reviewCount: 0,
    nextReviewAtMs: null,
    lastReviewedAtMs: null,
    notebookModule: module,
    notebookInk: text(body.notebookInk),
    notebookLayout: layout,
    notebookQuestionInk: questionInk,
  }
  await queueLocalEntity(localDb.notes, 'note', entity, current ? 'update' : 'create', current?.version ?? null)
  const pendingSync = await localDb.pendingOperations.where('entityId').equals(entity.id)
    .filter(item => item.entityType === 'note')
    .count()
  return json({ ok: true, notebook: legacyNote(entity), synced: pendingSync === 0 })
}

/** Append-only attempt entities avoid lost increments when two devices practise offline. */
async function handleQuestionAttempts(request: Request) {
  const identity = await ensureLocalIdentity()
  if (request.method === 'GET') {
    const records = active(await localDb.notes.where('[workspaceId+notebookModule]').equals([identity.workspaceId, '__question_practice__']).toArray(), identity.workspaceId)
      .filter(note => note.type === 'mistake_notebook' && note.notebookModule === '__question_practice__' && note.questionAttempt)
      .map(note => note.questionAttempt!)
    const pending = await localDb.pendingOperations.where('workspaceId').equals(identity.workspaceId)
      .filter(item => item.entityId.startsWith('qattempt_')).count()
    return json({ records, synced: pending === 0 })
  }
  if (request.method !== 'POST') return json({ error: '不支持的操作' }, 405)
  const body = await bodyOf(request)
  const attemptId = text(body.id)
  if (!/^[a-zA-Z0-9_-]{8,100}$/.test(attemptId)) return json({ error: '作答记录标识无效' }, 400)
  const id = `qattempt_${attemptId}`
  const existing = await localDb.notes.get(id)
  if (existing) {
    if (existing.workspaceId !== identity.workspaceId || existing.questionAttempt?.questionId !== text(body.questionId)) return json({ error: '记录不匹配' }, 409)
    return json({ record: existing.questionAttempt }) // Idempotent retry, not a new attempt.
  }
  const question = await localDb.notes.get(text(body.questionId))
  if (!question || question.deletedAtMs || question.workspaceId !== identity.workspaceId) return json({ error: '题目不存在' }, 404)
  const option = ['A', 'B', 'C', 'D'].includes(text(body.selectedOption)) ? text(body.selectedOption) as QuestionOptionKey : undefined
  if (question.correctOption && (!option || !question.questionOptions?.[option])) return json({ error: '请选择有效选项' }, 400)
  if (!question.correctOption && typeof body.correct !== 'boolean') return json({ error: '请标记作答结果' }, 400)
  const now = Date.now()
  const record = { id: attemptId, questionId: question.id, selectedOption: option,
    correct: question.correctOption ? option === question.correctOption : body.correct as boolean, attemptedAtMs: Math.max(now, Math.min(now + 1000, number(body.notBeforeMs))),
    ...(typeof body.durationSeconds==='number'&&Number.isFinite(body.durationSeconds)?{durationSeconds:Math.max(0,Math.min(604800,body.durationSeconds))}:{}),
    notebookSessionId: /^[a-zA-Z0-9_-]{8,100}$/.test(text(body.notebookSessionId)) ? text(body.notebookSessionId) : undefined,
    notebookStartedAtMs: Math.min(now, Math.max(now - 7 * 86400000, number(body.notebookStartedAtMs, now))),
    module: question.category && question.category !== '错题记录' ? question.category : question.tags.find(t => ['资料分析', '判断推理', '言语理解', '数量关系', '申论', '常识判断', '政治理论', '复盘整理'].includes(t)) || (['资料分析', '判断推理', '言语理解', '数量关系', '申论', '常识判断', '政治理论', '复盘整理'].includes(text(body.notebookModule)) ? text(body.notebookModule) : '综合') }
  const entity: Note = {
    ...versioned(identity, id, now), type: 'mistake_notebook', title: '题目作答记录', content: '',
    category: '__mistake_notebook__', tags: [], source: '', reviewStage: 0, reviewCount: 0,
    nextReviewAtMs: null, lastReviewedAtMs: null, notebookModule: '__question_practice__', questionAttempt: record,
  }
  await queueLocalEntity(localDb.notes, 'note', entity, 'create', null)
  const pending = await localDb.pendingOperations.where('entityId').equals(id).count()
  return json({ record, synced: pending === 0 }, 201)
}

async function handleReviewLogs(request: Request) {
  const identity = await ensureLocalIdentity()
  if (request.method === 'POST') {
    const body = await bodyOf(request)
    const now = Date.now()
    const logId = `rlog_${crypto.randomUUID()}`
    const log = {
      id: logId,
      workspaceId: identity.workspaceId,
      ownerUserId: identity.userId,
      createdAtMs: now,
      updatedAtMs: now,
      deletedAtMs: null,
      version: 1,
      noteId: text(body.noteId),
      deviceId: identity.deviceId,
      reviewedAt: text(body.reviewedAt) || new Date().toISOString(),
      rating: Number(body.rating) as 1 | 2 | 3 | 4,
      durationMs: body.durationMs ? Number(body.durationMs) : null,
      stateBefore: text(body.stateBefore) || 'new',
      stateAfter: text(body.stateAfter) || 'review',
      elapsedDays: Number(body.elapsedDays) || 0,
      scheduledDays: Number(body.scheduledDays) || 0,
      algorithmVersion: 'fsrs-6' as const,
      parameterVersion: 'v1',
    }
    await queueLocalEntity(localDb.reviewLogs, 'review_log', log, 'create', null)
    return json({ ok: true, log })
  }
  return json({ error: '不支持的操作' }, 405)
}

async function handlePractice(request: Request, id?: string) {
  const identity = await ensureLocalIdentity()
  if (id) {
    if (request.method === 'DELETE') {
      await softDeleteLocalEntity(localDb.practiceRecords, 'practice_record', id)
      return json({ ok: true })
    }
    return json({ error: '不支持的操作' }, 405)
  }
  if (request.method === 'GET') {
    const items = active(await localDb.practiceRecords.toArray(), identity.workspaceId).map(toLegacyPractice)
    return json({ records: items })
  }
  const body = await bodyOf(request)
  const totalCount = Math.max(1, Math.round(number(body.totalCount)))
  const correctCount = Math.max(0, Math.min(totalCount, Math.round(number(body.correctCount))))
  const now = Date.now()
  const entity: PracticeRecord = {
    ...versioned(identity, createEntityId('practice'), now),
    module: text(body.module) || '综合',
    correctCount,
    totalCount,
    durationMinutes: Math.max(0, number(body.durationMinutes)),
    note: text(body.note),
  }
  await putLocalEntity(localDb.practiceRecords, 'practice_record', entity, 'create', null)
  return json({ record: toLegacyPractice(entity) }, 201)
}

async function handleStats(url: URL) {
  const identity = await ensureLocalIdentity()
  const [sessions, todos, notes, practices, reviewLogs] = await Promise.all([
    localDb.studySessions.toArray(),
    localDb.todos.toArray(),
    localDb.notes.toArray(),
    localDb.practiceRecords.toArray(),
    localDb.reviewLogs.toArray(),
  ])
  const scopedSessions = active(sessions, identity.workspaceId)
  const scopedTodos = active(todos, identity.workspaceId)
  const scopedNotes = active(notes, identity.workspaceId)
  const scopedPractices = active(practices, identity.workspaceId)
  const scopedReviewLogs = active(reviewLogs, identity.workspaceId)
  if (url.searchParams.get('comprehensive') === 'true') {
    return json(buildComprehensiveStats(scopedSessions, scopedPractices, scopedNotes))
  }
  return json(buildPeriodStats(
    url.searchParams.get('date') || formatLocalDate(),
    url.searchParams.get('period') || 'day',
    scopedSessions,
    scopedTodos,
    scopedNotes,
    scopedPractices,
    scopedReviewLogs,
  ))
}

async function handleSettings(request: Request) {
  const identity = await ensureLocalIdentity()
  let current = await localDb.userSettings.get(identity.userId)
  if (!current) {
    // Auto-create settings for new accounts (e.g. first login after registration)
    const now = Date.now()
    current = {
      id: identity.userId,
      workspaceId: identity.workspaceId,
      ownerUserId: identity.userId,
      displayName: '',
      email: '',
      avatarUrl: null,
      statusSignature: '每一次专注，都让上岸更近一步。',
      examName: '我的考试目标',
      examDate: '',
      mockName: '',
      mockDate: '',
      examType: '',
      studyModules: ['资料分析', '判断推理', '言语理解', '数量关系', '申论', '常识判断', '政治理论', '复盘整理'],
      focusTheme: 'standard',
      createdAtMs: now,
      updatedAtMs: now,
      deletedAtMs: null,
      version: 0,
    }
    await putLocalEntity(localDb.userSettings, 'user_settings', current, 'create', null)
  }
  if (request.method === 'GET') return json({ settings: current })
  const body = await bodyOf(request)
  const now = Date.now()
  const updated: UserSettings = {
    ...current,
    displayName: body.displayName === undefined ? current.displayName : text(body.displayName),
    email: body.email === undefined ? current.email : text(body.email),
    statusSignature: body.statusSignature === undefined ? current.statusSignature : text(body.statusSignature),
    examName: body.examName === undefined ? current.examName : text(body.examName),
    examDate: body.examDate === undefined ? current.examDate : text(body.examDate),
    mockName: body.mockName === undefined ? current.mockName : text(body.mockName),
    mockDate: body.mockDate === undefined ? current.mockDate : text(body.mockDate),
    examType: body.examType === undefined ? current.examType : text(body.examType),
    studyModules: body.studyModules === undefined ? current.studyModules : stringList(body.studyModules),
    focusTheme: body.focusTheme === undefined ? current.focusTheme : text(body.focusTheme),
    updatedAtMs: now,
  }
  await putLocalEntity(localDb.userSettings, 'user_settings', updated, 'update', current.version)
  return json({ ok: true, settings: updated })
}

async function handleDailyNews() {
  const date = formatLocalDate()
  const cached = await localDb.dailyNews.get(date)
  try {
    const response = await remoteApiRequest('news/daily')
    if (!response.headers.get('Content-Type')?.includes('application/json')) {
      throw new Error('尚未连接 Cloudflare 新闻服务，请先在设置中配置 Worker API 地址')
    }
    const payload = await response.json() as Record<string, unknown>
    if (!response.ok) {
      if (cached) return json({ news: cached.payload, cached: true })
      return json({
        error: typeof payload.error === 'string' ? payload.error : '今日热点更新失败',
        code: payload.needsKey ? 'MISSING_API_KEY' : 'NEWS_FETCH_FAILED',
      }, response.status)
    }
    const news = {
      date: payload.date,
      national: payload.national,
      jiangsu: payload.jiangsu,
    }
    await localDb.dailyNews.put({ date, payload: news, generatedAtMs: Date.now() })
    return json({ news, cached: Boolean(payload.cached) })
  } catch (error) {
    if (cached) return json({ news: cached.payload, cached: true, offline: true })
    return json({ error: error instanceof Error ? error.message : '离线且没有今日新闻缓存', code: 'OFFLINE' }, 503)
  }
}

export async function handleLocalApi(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const absolute = typeof input === 'string'
    ? new URL(input, globalThis.location?.origin || 'http://localhost')
    : input instanceof URL
      ? input
      : new URL(input.url)
  const request = input instanceof Request ? input : new Request(absolute, init)
  const path = absolute.pathname
  const idMatch = (prefix: string) => path.startsWith(`${prefix}/`) ? decodeURIComponent(path.slice(prefix.length + 1)) : undefined

  try {
    if (path.startsWith('/api/auth/')) return handleAuth(path, request)
    if (path === '/api/focus/heatmap') return handleFocusHeatmap()
    if (path === '/api/focus') return handleFocus(absolute, request)
    if (path === '/api/todos' || idMatch('/api/todos')) return handleTodos(absolute, request, idMatch('/api/todos'))
    if (path === '/api/recurring' || idMatch('/api/recurring')) return handleRecurring(request, idMatch('/api/recurring'))
    if (path === '/api/inspirations' || idMatch('/api/inspirations')) return handleNotes(absolute, request, idMatch('/api/inspirations'))
    if (path === '/api/inspiration-tags' || idMatch('/api/inspiration-tags')) return handleTags(request, idMatch('/api/inspiration-tags'))
    if (path === '/api/review') return handleReview(absolute, request)
    if (path === '/api/notebooks' || path.startsWith('/api/notebooks/')) return handleLocalNotebookApi(request, absolute, path === '/api/notebooks' ? undefined : decodeURIComponent(path.slice('/api/notebooks/'.length)))
    if (path === '/api/mistake-notebook') return handleMistakeNotebook(absolute, request)
    if (path === '/api/mistake-notebook/attempts') return handleQuestionAttempts(request)
    if (path === '/api/mistake-notebook/progress') {
      const module = text(absolute.searchParams.get('module'))
      if (!module) return json({ error: '请选择模块' }, 400)
      const body = await bodyOf(request)
      const bookId = absolute.searchParams.get('bookId')
      if (bookId) {
        const identity = await ensureLocalIdentity(), book = await localDb.notes.get(bookId)
        if (!book || book.workspaceId !== identity.workspaceId || book.deletedAtMs || !book.notebookBook) return json({ error: '错题本不存在' }, 404)
        if (request.method === 'GET') return json({ progress: book.notebookProgress || {} })
        if (request.method !== 'PUT') return json({ error: '不支持的操作' }, 405)
        const saved = await patchLocalNotebook(bookId, latest => ({ notebookProgress: { ...latest.notebookProgress, ...(typeof body.lastQuestionId === 'string' ? { lastQuestionId: body.lastQuestionId } : {}), resetAt: { ...latest.notebookProgress?.resetAt, ...(body.resetAt && typeof body.resetAt === 'object' ? Object.fromEntries(Object.entries(body.resetAt).filter(([, n]) => typeof n === 'number' && Number.isFinite(n) && n >= 0)) as Record<string, number> : {}) } } }))
        return json({ progress: saved.notebookProgress })
      }
      const patch = request.method === 'PUT' ? { ...(typeof body.lastQuestionId === 'string' ? { lastQuestionId: text(body.lastQuestionId) } : {}), ...(body.resetAt && typeof body.resetAt === 'object' ? { resetAt: Object.fromEntries(Object.entries(body.resetAt).filter(([, n]) => typeof n === 'number' && Number.isFinite(n))) as Record<string, number> } : {}) } : undefined
      return json({ progress: await notebookProgress(module, patch) })
    }
    if (path === '/api/mistake-notebook/session' && request.method === 'POST') {
      const body = await bodyOf(request)
      if (!/^[a-zA-Z0-9_-]{8,100}$/.test(text(body.id)) || !['start', 'end'].includes(text(body.action))) return json({ error: '会话无效' }, 400)
      await notebookVisit(body.action as 'start' | 'end', { id: text(body.id), module: text(body.module), startedAtMs: Math.min(Date.now(), Math.max(Date.now() - 7 * 86400000, number(body.startedAtMs, Date.now()))) })
      return json({ ok: true })
    }
    if (path === '/api/review-logs') return handleReviewLogs(request)
    if (path === '/api/practice' || idMatch('/api/practice')) return handlePractice(request, idMatch('/api/practice'))
    if (path === '/api/stats') return handleStats(absolute)
    if (path === '/api/user/settings') return handleSettings(request)
    if (path === '/api/daily-news') return handleDailyNews()
    return json({ error: `本地接口不存在：${path}` }, 404)
  } catch (error) {
    console.error('Local API error', error)
    return json({ error: error instanceof Error ? error.message : '本地数据操作失败' }, 500)
  }
}
